import apiService from "./apiService";
import { OAuthContact } from "../types/onboarding";
import {
  ContactList,
  ContactListWithCount,
  CreateContactListRequest,
  UpdateContactListRequest,
  ContactListResponse,
  ContactListsResponse,
} from "../types/contactList";

export interface ContactsResponse {
  contacts: OAuthContact[];
  provider: string;
  totalCount: number;
}

export interface ContactsError {
  error: string;
  details?: string;
}

// Interface for user's saved contacts (from database)
export interface UserContact {
  id: string;
  user_id: string;
  email: string;
  name?: string;
  settings?: {
    provider?: string;
    external_id?: string;
    phone?: string;
    organization?: string;
  };
  created_at: string;
}

export interface CreateContactRequest {
  email: string;
  name?: string;
  settings?: any;
}

export class ContactsService {
  /**
   * Get user's existing contacts from database
   */
  static async getUserContacts(): Promise<UserContact[]> {
    try {
      const response = await apiService.get("/contacts");
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user contacts"
      );
    }
  }

  /**
   * Create a new contact
   */
  static async createContact(
    contact: CreateContactRequest
  ): Promise<UserContact> {
    try {
      console.log("ContactsService.createContact:request", contact);
      const response = await apiService.post("/contacts", contact);
      console.log("ContactsService.createContact:success", response.status);
      return response.data;
    } catch (error: any) {
      console.error("ContactsService.createContact:error", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw new Error(
        error.response?.data?.error || "Failed to create contact"
      );
    }
  }

  /**
   * Update an existing contact
   */
  static async updateContact(
    id: string,
    contact: Partial<CreateContactRequest>
  ): Promise<UserContact> {
    try {
      const response = await apiService.patch(`/contacts/${id}`, contact);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to update contact"
      );
    }
  }

  /**
   * Delete a contact
   */
  static async deleteContact(id: string): Promise<void> {
    try {
      await apiService.delete(`/contacts/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to delete contact"
      );
    }
  }

  /**
   * Get contacts from all connected OAuth providers
   */
  static async getOAuthContacts(): Promise<ContactsResponse> {
    try {
      const response = await apiService.get("/contacts/oauth");
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch OAuth contacts"
      );
    }
  }

  /**
   * Get contacts from a specific OAuth provider
   */
  static async getContactsFromProvider(
    provider: string
  ): Promise<ContactsResponse> {
    try {
      const response = await apiService.get(`/contacts/oauth/${provider}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          `Failed to fetch contacts from ${provider}`
      );
    }
  }

  /**
   * Save selected contacts to the database
   */
  static async saveSelectedContacts(
    contacts: OAuthContact[]
  ): Promise<{ success: boolean; savedCount: number }> {
    try {
      const response = await apiService.post("/contacts/save-selected", {
        contacts,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to save selected contacts"
      );
    }
  }

  /**
   * Sync all contacts from OAuth providers to database
   */
  static async syncAllContacts(): Promise<{
    success: boolean;
    syncedCount: number;
  }> {
    try {
      const response = await apiService.post("/contacts/sync");
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || "Failed to sync contacts");
    }
  }

  // ============ CONTACT LISTS METHODS ============

  /**
   * Get all contact lists for the user
   */
  static async getContactLists(): Promise<ContactListWithCount[]> {
    try {
      const response = await apiService.get("/contacts/lists");
      return response.data.lists || [];
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch contact lists"
      );
    }
  }

  /**
   * Get a single contact list
   */
  static async getContactList(listId: string): Promise<ContactList> {
    try {
      const response = await apiService.get(`/contacts/lists/${listId}`);
      return response.data.list;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch contact list"
      );
    }
  }

  /**
   * Get contacts in a specific list
   */
  static async getContactsInList(listId: string): Promise<UserContact[]> {
    try {
      const response = await apiService.get(
        `/contacts/lists/${listId}/contacts`
      );
      return response.data.contacts || [];
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch contacts in list"
      );
    }
  }

  /**
   * Create a new contact list
   */
  static async createContactList(
    listData: CreateContactListRequest
  ): Promise<ContactList> {
    try {
      const response = await apiService.post("/contacts/lists", listData);
      return response.data.list;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to create contact list"
      );
    }
  }

  /**
   * Update a contact list
   */
  static async updateContactList(
    listId: string,
    updateData: UpdateContactListRequest
  ): Promise<ContactList> {
    try {
      const response = await apiService.patch(
        `/contacts/lists/${listId}`,
        updateData
      );
      return response.data.list;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to update contact list"
      );
    }
  }

  /**
   * Delete a contact list
   */
  static async deleteContactList(listId: string): Promise<void> {
    try {
      await apiService.delete(`/contacts/lists/${listId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to delete contact list"
      );
    }
  }

  /**
   * Add contacts to a list
   */
  static async addContactsToList(
    listId: string,
    contactIds: string[]
  ): Promise<void> {
    try {
      await apiService.post(`/contacts/lists/${listId}/contacts`, {
        contact_ids: contactIds,
      });
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to add contacts to list"
      );
    }
  }

  /**
   * Remove a contact from a list
   */
  static async removeContactFromList(
    listId: string,
    contactId: string
  ): Promise<void> {
    try {
      await apiService.delete(
        `/contacts/lists/${listId}/contacts/${contactId}`
      );
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to remove contact from list"
      );
    }
  }

  /**
   * Reorder contact lists
   */
  static async reorderContactLists(listIds: string[]): Promise<ContactList[]> {
    try {
      const response = await apiService.post("/contacts/lists/reorder", {
        list_ids: listIds,
      });
      return response.data.lists || [];
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to reorder contact lists"
      );
    }
  }

  /**
   * Search available contacts to add to a list
   */
  static async searchAvailableContacts(
    listId: string,
    query?: string
  ): Promise<UserContact[]> {
    try {
      const params = query ? { q: query } : {};
      const response = await apiService.get(
        `/contacts/lists/${listId}/available`,
        { params }
      );
      return response.data.contacts || [];
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to search available contacts"
      );
    }
  }
}
